// CallLogReviewModal — review a pending_review call log before ingestion.
// Claude has already run (same flow for both RC calls and Granola meetings).
// The modal lets the user correct the contact, edit the summary, and link entities.
// Contact correction is shown first and prominently — the most common correction needed.
// All contact fields use ContactSearchOrCreate, so attendees who aren't in the
// OS yet (e.g. a new meeting participant) can be created on the spot.

import { useState } from 'react'
import { X, User, Phone, RefreshCw, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'
import ContactSearchOrCreate from '../ui/ContactSearchOrCreate'
import MultiEntityPicker from '../ui/MultiEntityPicker'
import { useToast } from '../../contexts/ToastContext'
import { useAuth } from '../../contexts/AuthContext'
import { displayDate } from '../../lib/dateUtil.js'

const SENTIMENT_COLOR = {
  warm:    'bg-green-100 text-green-800',
  neutral: 'bg-warm-100 text-warm-700',
  cool:    'bg-blue-100 text-blue-800',
  tense:   'bg-red-100 text-red-800',
}

// ── Main modal ─────────────────────────────────────────────────────────────────

export default function CallLogReviewModal({ callLog, onConfirmed, onDismiss }) {
  const toast = useToast()
  const { user } = useAuth()
  const myEmail = user?.email?.toLowerCase() || null

  const [contact, setContact] = useState(
    callLog?.contact_id
      ? { id: callLog.contact_id, name: callLog.contact_name || callLog.raw_caller_name || '' }
      : null
  )
  const [doReplace, setDoReplace] = useState(false)

  // People on the call — seeded from Granola attendees (non-self), editable:
  // link unmatched attendees to contacts, remove wrong ones, add missing ones.
  const [people, setPeople] = useState(() =>
    (callLog?.raw_granola_data?.attendees || [])
      .filter(a => !a.self)
      .map((a, i) => ({
        key: `g${i}`,
        contact_id: a.contact_id || null,
        name: a.name || null,
        email: a.email || null,
        // Backend flags known principals; also recognise the reviewer themselves
        // so rows ingested before principal-tagging still don't read "not in OS".
        is_principal: !!a.is_principal || (!!myEmail && a.email?.toLowerCase() === myEmail),
      }))
  )
  const [linkingKey, setLinkingKey]     = useState(null)
  const [addingPerson, setAddingPerson] = useState(false)

  // Summary
  const [summary, setSummary]               = useState(callLog?.summary || callLog?.notes || '')
  const [transcriptOpen, setTranscriptOpen] = useState(false)

  // Entity links
  const [linkedCompanies,     setLinkedCompanies]     = useState([])
  const [linkedDeals,         setLinkedDeals]         = useState([])
  const [linkedProjects,      setLinkedProjects]       = useState([])
  const [linkedProperties,    setLinkedProperties]     = useState([])
  const [linkedOpportunities, setLinkedOpportunities] = useState([])

  const [saving, setSaving] = useState(false)

  if (!callLog) return null

  const rawName    = callLog.raw_caller_name || callLog.contact_name || 'Unknown'
  const rawNumber  = callLog.raw_caller_number
  const isNewContact = contact && contact.id !== callLog.contact_id
  const nameChanged  = isNewContact && rawName && contact?.name && rawName !== contact.name

  const sourceLabel  = callLog.source === 'granola' ? 'Granola meeting' : 'RingCentral call'
  const isGranola    = callLog.source === 'granola'

  async function handleConfirm() {
    setSaving(true)
    try {
      const body = {
        callLogId:           callLog.id,
        contactId:           contact?.id   || null,
        contactName:         contact?.name || null,
        doReplace:           doReplace && nameChanged,
        oldName:             rawName,
        newName:             contact?.name || null,
        linkedContacts:      [...new Set(people.filter(p => p.contact_id).map(p => p.contact_id))],
        linkedCompanies:     linkedCompanies.map(x => x.id),
        linkedDeals:         linkedDeals.map(x => x.id),
        linkedProjects:      linkedProjects.map(x => x.id),
        linkedProperties:    linkedProperties.map(x => x.id),
        linkedOpportunities: linkedOpportunities.map(x => x.id),
        summary,
      }

      const res = await fetch('/api/confirm-call-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(await res.text())
      toast.success('Call log confirmed.')
      onConfirmed(callLog.id)
    } catch (err) {
      toast.error('Could not confirm: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const duration = callLog.duration_minutes
    ? `${callLog.duration_minutes} min`
    : null

  const callDate = callLog.contact_date
    ? displayDate(callLog.contact_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-warm-200 bg-warm-50 rounded-t-xl">
          <div className="flex items-center gap-2.5 min-w-0">
            <Phone className="w-4 h-4 text-navy-600 shrink-0" />
            <span className="font-semibold text-navy-800 text-sm truncate">
              {`Review ${sourceLabel}`}
            </span>
            {callDate && <span className="text-warm-500 text-xs shrink-0">· {callDate}</span>}
            {duration  && <span className="text-warm-500 text-xs shrink-0">· {duration}</span>}
            {callLog.sentiment && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium shrink-0 ${SENTIMENT_COLOR[callLog.sentiment] || ''}`}>
                {callLog.sentiment}
              </span>
            )}
          </div>
          <button onClick={onDismiss} className="text-warm-400 hover:text-warm-700 transition-colors ml-2 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* ── Section 1: Identity — always first and prominent ───────────── */}
          <div className="px-5 pt-4 pb-4 border-b border-warm-100">
            <p className="text-sm font-semibold text-warm-700 mb-1.5">Who was this call with?</p>

            {/* Raw matched info — what RC or Granola thought */}
            {(rawNumber || (rawName && rawName !== 'Unknown')) && (
              <p className="text-xs text-warm-400 mb-2.5">
                {isGranola ? 'Matched from:' : 'Matched from:'}{' '}
                <span className="text-warm-600">
                  {rawName && rawName !== 'Unknown' ? rawName : ''}
                  {rawNumber ? ` (${rawNumber})` : ''}
                </span>
              </p>
            )}

            <ContactSearchOrCreate
              value={contact}
              onChange={setContact}
              placeholder="Search or correct contact…"
            />

            {nameChanged && (
              <label className="flex items-center gap-2 mt-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={doReplace}
                  onChange={e => setDoReplace(e.target.checked)}
                  className="rounded border-warm-300 text-navy-600 focus:ring-navy-500"
                />
                <span className="text-xs text-warm-700">
                  Replace <strong>"{rawName}"</strong> with <strong>"{contact.name}"</strong> throughout the notes
                </span>
              </label>
            )}

            {/* People on this call — editable. Linked contacts get this meeting
                on their Activity timeline via call_log.linked_contacts. */}
            <div className="mt-3 pt-2.5 border-t border-warm-100">
              <p className="text-xs text-warm-400 mb-1.5">
                People on this {isGranola ? 'meeting' : 'call'} — linked contacts get it on their Activity timeline:
              </p>
              <div className="flex flex-col gap-1.5">
                {people.map(p => (
                  <div key={p.key} className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2 py-1 border ${
                      p.contact_id
                        ? 'font-medium text-navy-700 bg-navy-50 border-navy-200'
                        : p.is_principal
                          ? 'font-medium text-green-800 bg-green-50 border-green-200'
                          : 'text-warm-600 bg-warm-50 border-warm-200'
                    }`}>
                      <User className={`w-3 h-3 ${
                        p.contact_id ? 'text-navy-400' : p.is_principal ? 'text-green-500' : 'text-warm-400'
                      }`} />
                      {p.name || p.email || 'Unknown'}
                      {!p.contact_id && !p.is_principal && <span className="text-warm-400">· not saved yet</span>}
                      {p.is_principal && <span className="text-green-600">· team</span>}
                    </span>
                    {!p.contact_id && !p.is_principal && (
                      <button
                        onClick={() => setLinkingKey(linkingKey === p.key ? null : p.key)}
                        className="text-xs text-navy-600 hover:text-navy-800 transition-colors"
                      >
                        {linkingKey === p.key ? 'Cancel' : 'Link to contact'}
                      </button>
                    )}
                    <button
                      onClick={() => setPeople(prev => prev.filter(x => x.key !== p.key))}
                      className="text-warm-300 hover:text-red-500 transition-colors ml-auto"
                      title="Remove from this call"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}

                {linkingKey && (() => {
                  const linkee = people.find(x => x.key === linkingKey)
                  return (
                    <ContactSearchOrCreate
                      key={linkingKey}
                      value={null}
                      onChange={c => {
                        if (!c?.id) return
                        setPeople(prev => prev.map(x => x.key === linkingKey ? { ...x, contact_id: c.id, name: c.name } : x))
                        setLinkingKey(null)
                      }}
                      initialQuery={linkee?.name || ''}
                      createDefaults={{ email: linkee?.email || '' }}
                      placeholder={`Link "${linkee?.name || linkee?.email || 'attendee'}" to a contact…`}
                    />
                  )
                })()}

                {addingPerson ? (
                  <ContactSearchOrCreate
                    value={null}
                    onChange={c => {
                      if (!c?.id) return
                      setPeople(prev => prev.some(x => x.contact_id === c.id)
                        ? prev
                        : [...prev, { key: `c-${c.id}`, contact_id: c.id, name: c.name, email: null }])
                      setAddingPerson(false)
                    }}
                    placeholder="Add or create a contact for this call…"
                  />
                ) : (
                  <button
                    onClick={() => setAddingPerson(true)}
                    className="self-start text-xs text-navy-600 hover:text-navy-800 transition-colors mt-0.5"
                  >
                    + Add person
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 2: Summary ─────────────────────────────────────────── */}
          <>
            <div className="px-5 pt-4 pb-4 border-b border-warm-100">
              <p className="text-xs font-semibold text-warm-600 uppercase tracking-wide mb-2.5">
                Summary
              </p>
              <textarea
                value={summary}
                onChange={e => setSummary(e.target.value)}
                rows={4}
                className="input-field text-sm w-full resize-none"
                placeholder="Edit the auto-generated summary if needed…"
              />

              {callLog.transcript && (
                <div className="mt-2">
                  <button
                    onClick={() => setTranscriptOpen(v => !v)}
                    className="flex items-center gap-1 text-xs text-navy-600 hover:text-navy-800 transition-colors"
                  >
                    {transcriptOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    {transcriptOpen ? 'Hide' : 'Show'} transcript
                  </button>
                  {transcriptOpen && (
                    <pre className="mt-2 p-3 bg-warm-50 rounded-md text-xs text-warm-700 whitespace-pre-wrap max-h-48 overflow-y-auto border border-warm-200 font-mono leading-relaxed">
                      {callLog.transcript}
                    </pre>
                  )}
                </div>
              )}

              {callLog.janet_flags?.length > 0 && (
                <div className="mt-3 flex flex-col gap-1">
                  {callLog.janet_flags.map((flag, i) => (
                    <p key={i} className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 border border-amber-200">
                      ⚑ {flag}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </>

          {/* ── Section 3: Topics / Entity Links (both modes) ───────────── */}
          <div className="px-5 pt-4 pb-5">
            <p className="text-xs font-semibold text-warm-600 uppercase tracking-wide mb-3">
              What was discussed? Link to entities
            </p>
            <p className="text-xs text-warm-500 mb-3">
              Each entity you link will show this {isGranola ? 'meeting' : 'call'} in its Activity timeline.
            </p>

            <div className="flex flex-col gap-3">
              <div>
                <label className="block text-xs text-warm-600 mb-1 font-medium">Companies</label>
                <MultiEntityPicker
                  value={linkedCompanies}
                  onChange={setLinkedCompanies}
                  table="companies"
                  searchCols={['name']}
                  labelFn={r => r.name}
                  placeholder="Search companies…"
                  selectCols="id, name"
                />
              </div>

              <div>
                <label className="block text-xs text-warm-600 mb-1 font-medium">Deals</label>
                <MultiEntityPicker
                  value={linkedDeals}
                  onChange={setLinkedDeals}
                  table="deals"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  placeholder="Search deals…"
                  selectCols="id, title, deal_type, status"
                />
              </div>

              <div>
                <label className="block text-xs text-warm-600 mb-1 font-medium">Projects</label>
                <MultiEntityPicker
                  value={linkedProjects}
                  onChange={setLinkedProjects}
                  table="projects"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  placeholder="Search projects…"
                  selectCols="id, title, status"
                />
              </div>

              <div>
                <label className="block text-xs text-warm-600 mb-1 font-medium">Properties</label>
                <MultiEntityPicker
                  value={linkedProperties}
                  onChange={setLinkedProperties}
                  table="properties"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  placeholder="Search properties…"
                  selectCols="id, title, status"
                />
              </div>

              <div>
                <label className="block text-xs text-warm-600 mb-1 font-medium">Opportunities</label>
                <MultiEntityPicker
                  value={linkedOpportunities}
                  onChange={setLinkedOpportunities}
                  table="opportunities"
                  searchCols={['title']}
                  labelFn={r => r.title}
                  placeholder="Search opportunities…"
                  selectCols="id, title, status"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-warm-200 bg-warm-50 rounded-b-xl flex items-center justify-between gap-3">
          <button
            onClick={onDismiss}
            className="text-sm text-warm-600 hover:text-warm-800 transition-colors"
          >
            Skip for now
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            {saving
              ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              : <CheckCircle className="w-3.5 h-3.5" />
            }
            {saving ? 'Confirming…' : 'Confirm & Ingest'}
          </button>
        </div>
      </div>
    </div>
  )
}
